/**
 * Auth failures → the surfaces that show them.
 *
 * There are three surfaces on an auth form, and which one a failure lands on is a product
 * decision, not a rendering detail (the password-reset screens use the same three):
 *
 * | Surface          | Carries                                                                  |
 * |------------------|--------------------------------------------------------------------------|
 * | `banner`         | Anything the trainer cannot fix in one specific field.                    |
 * | `emailError`     | Only failures about the address itself — today, a duplicate.              |
 * | `passwordInvalid`| A ring on the password field and **no message**: bad credentials are one message, in the banner. |
 *
 * The mapping is reproduced from the mobile app (`features/auth/api/authErrors.ts` +
 * `lib/resolveAuthError.ts`), because the two clients must fail identically for the same account.
 * Four rules in it are load-bearing:
 *
 * 1. **A duplicate address is anchored on the email field, not the banner.** It is the one failure
 *    where the trainer's next action is editing a specific field, and a banner puts the sentence
 *    far away from the thing to change.
 * 2. **A bad credential shows its message once.** The password field gets the ring, the banner gets
 *    the words. Saying it twice reads as two problems.
 * 3. **A 5xx never shows the server's text.** `detail` on a 500 is an exception message; it is not
 *    written for a trainer and can name internals. Our own copy replaces it.
 * 4. **An unmapped registration failure must not fall through to the sign-in copy.** A 500
 *    `unexpected_failure` while sending the confirmation email would otherwise render "wrong email
 *    or password" on the sign-up screen — a real defect on mobile before it was fixed.
 *
 * Pure. `t` arrives as a parameter; nothing here imports i18next, React or the DOM. The only
 * runtime import is Supabase's own `isAuthError` type guard, which reads one property.
 */

import { isAuthError } from '@supabase/supabase-js';

import { ApiError } from '~/shared/api/mutator';

/** Which flow raised the failure. It decides only the unmapped-failure copy — see rule 4 above. */
export type AuthFlow = 'signIn' | 'signUp' | 'reset' | 'social';

export interface AuthErrorSurfaces {
  /** Form-level message. `null` when the failure belongs to a field instead. */
  banner: string | null;
  /** Message anchored under the email field. */
  emailError: string | null;
  /** Ring on the password field, deliberately without a message of its own. */
  passwordInvalid: boolean;
}

export const NO_AUTH_ERROR: AuthErrorSurfaces = {
  banner: null,
  emailError: null,
  passwordInvalid: false,
};

/** Only the key is needed; the model layer never holds an i18next instance. */
type Translate = (key: string) => string;

const HTTP_UNAUTHORIZED = 401;
const HTTP_CONFLICT = 409;
const HTTP_RATE_LIMITED = 429;
const HTTP_SERVER_ERROR = 500;

/** Locale keys, all of which already exist in `src/locales/pl.json`. */
const KEY = {
  badCredentials: 'auth.si.err',
  rateLimited: 'auth.si.rate',
  duplicateEmail: 'auth.e.dup',
  network: 'auth.e.net',
  weakPassword: 'auth.e.weakpw',
  samePassword: 'auth.e.samepw',
  signUpFailed: 'auth.e.signup',
  socialFailed: 'auth.social.err',
  resetCodeRejected: 'auth.fp.otpexp',
} as const;

/** The generic message for a failure this build cannot classify, per flow. */
function unmappedKey(flow: AuthFlow): string {
  switch (flow) {
    case 'social':
      return KEY.socialFailed;
    // Rule 4: never the sign-in copy.
    case 'signUp':
      return KEY.signUpFailed;
    // A reset that failed for a reason this build cannot name is, to the trainer, a code that did
    // not work — the one thing on that screen they can act on is asking for another.
    case 'reset':
      return KEY.resetCodeRejected;
    case 'signIn':
      return KEY.badCredentials;
  }
}

/** A Supabase auth error code → a locale key. `undefined` means "not classified here". */
function keyForSupabaseCode(code: string | undefined): string | undefined {
  switch (code) {
    // An unconfirmed address is deliberately folded into the bad-credentials message rather than
    // told apart: on the sign-in screen the two are the same dead end, and distinguishing them
    // would leak whether an address is registered.
    case 'invalid_credentials':
    case 'email_not_confirmed':
      return KEY.badCredentials;
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
    case 'over_sms_send_rate_limit':
      return KEY.rateLimited;
    case 'user_already_exists':
    case 'email_exists':
      return KEY.duplicateEmail;
    case 'weak_password':
      return KEY.weakPassword;
    case 'same_password':
      return KEY.samePassword;
    // The recovery code: typed wrong, used already, or older than the project's otp_expiry.
    case 'otp_expired':
    case 'otp_disabled':
      return KEY.resetCodeRejected;
    // A refused fetch reaches us as an AuthError with no status and no code, so this covers the
    // "Supabase is unreachable" case too, via `unmappedKey` below rather than here.
    case 'request_timeout':
      return KEY.network;
    default:
      return undefined;
  }
}

/** A ProblemDetails status → a locale key. `undefined` means "fall through to the body text". */
function keyForApiStatus(status: number, flow: AuthFlow): string | undefined {
  // Rule 3, first: a 5xx is replaced before anything else can decide to render its body.
  if (status >= HTTP_SERVER_ERROR) return KEY.network;
  if (status === HTTP_RATE_LIMITED) return KEY.rateLimited;
  if (status === HTTP_CONFLICT) return KEY.duplicateEmail;
  if (status === HTTP_UNAUTHORIZED && flow === 'signIn') return KEY.badCredentials;
  return undefined;
}

/** Place a resolved key on the surface it belongs to. */
function surfacesForKey(key: string, t: Translate): AuthErrorSurfaces {
  // Rule 1.
  if (key === KEY.duplicateEmail) return { banner: null, emailError: t(key), passwordInvalid: false };
  // Rule 2: the ring, and the words exactly once — in the banner.
  if (key === KEY.badCredentials) return { banner: t(key), emailError: null, passwordInvalid: true };
  return { banner: t(key), emailError: null, passwordInvalid: false };
}

/**
 * Split a ProblemDetails validation map across the fields it names. A key the form has no field
 * for cannot be anchored, so it goes to the banner rather than being dropped.
 */
function surfacesForFieldErrors(errors: Record<string, string[]>): AuthErrorSurfaces | null {
  const emailMessages: string[] = [];
  const passwordMessages: string[] = [];
  const otherMessages: string[] = [];

  for (const [field, messages] of Object.entries(errors)) {
    // The backend emits two shapes under `errors`. Model-state failures give the documented
    // `Record<string, string[]>`, keyed by field. A `ValidationException` gives a BARE ARRAY of
    // strings (`DomainExceptionHandler` sets `Extensions["errors"] = new[] { ex.Message }`), and
    // `Object.entries` turns that into ["0", "the message"] — a numeric key and a plain string.
    // Spreading a string pushes it one character at a time, which renders the banner as one letter
    // per line. Normalising here keeps that shape difference out of every caller.
    const list = Array.isArray(messages) ? messages : [messages];
    const name = field.toLowerCase();
    if (name.includes('email')) emailMessages.push(...list);
    else if (name.includes('password')) passwordMessages.push(...list);
    else otherMessages.push(...list);
  }

  if (emailMessages.length === 0 && passwordMessages.length === 0 && otherMessages.length === 0) {
    return null;
  }

  return {
    // A password validation message IS shown — it is specific and actionable, unlike the
    // deliberately vague bad-credentials text that rule 2 keeps in one place.
    banner: [...otherMessages, ...passwordMessages].join('\n') || null,
    emailError: emailMessages.join('\n') || null,
    passwordInvalid: passwordMessages.length > 0,
  };
}

/**
 * The single entry point: a caught failure and the flow it came from, in — the three surfaces out.
 *
 * Every branch resolves to a locale key or to text the backend authored for a trainer. Nothing
 * reaches the screen as a raw exception message except a 4xx ProblemDetails body, which is written
 * for a user; a 5xx body never does (rule 3).
 */
export function resolveAuthErrorSurfaces(
  error: unknown,
  flow: AuthFlow,
  t: Translate,
): AuthErrorSurfaces {
  if (isAuthError(error)) {
    return surfacesForKey(keyForSupabaseCode(error.code) ?? unmappedKey(flow), t);
  }

  if (error instanceof ApiError) {
    const key = keyForApiStatus(error.status, flow);
    if (key !== undefined) return surfacesForKey(key, t);

    const fieldErrors = error.problem?.errors;
    const anchored = fieldErrors ? surfacesForFieldErrors(fieldErrors) : null;
    if (anchored) return anchored;

    const serverText = error.problem?.detail ?? error.problem?.title;
    return serverText
      ? { banner: serverText, emailError: null, passwordInvalid: false }
      : surfacesForKey(unmappedKey(flow), t);
  }

  // Not an auth error and not a ProblemDetails: a refused fetch, an abort, a timeout, a thrown
  // string. All of them mean "we could not reach anything", which is a connection message.
  return surfacesForKey(KEY.network, t);
}
